import { BaseApiService } from '@/shared/api/services/base.service';
import { authLogger } from '@/shared/lib/logger';
import { ClassTypes } from '@/shared/constants';
import type { ProfileData } from '@/types/profile';
import type {
  AuthCredentials,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ValidateResetTokenRequest,
  ResetPasswordRequest,
} from '@/types/auth';

/**
 * Auth wire shape mirrors /api/v1/user (UserProfileResource) plus
 * `meta.token`. Same wire shape for every user-returning endpoint, so the
 * client uses one decoding path. The Sanctum token rides on document-level
 * `meta.token` — never on `data.meta` — and `meta.default_project_id` is
 * present on googleSignIn / verifyEmailCode responses for new-user flows.
 */
interface AuthResponse {
  data: {
    type: string;
    id: string;
    attributes: ProfileData;
  };
  meta?: {
    token?: string;
    default_project_id?: string;
    /** True when this auth call created a NEW account. Optional — treat a
     *  missing value as "unknown", never as false-required. */
    is_new_user?: boolean;
    /** Present when adding a sign-in method merged another account in (ADR-001 §6). */
    account_merge?: { buildings: number; message: string } | null;
  };
}

/** Result of adding a sign-in method: a fresh session + optional merge text. */
export interface SignInMethodResult {
  credentials: AuthCredentials;
  mergeMessage: string | null;
}

interface ParsedProfile {
  user: AuthCredentials['user'];
  profileData: ProfileData;
}

function parseProfileFromAuthResponse(response: AuthResponse): ParsedProfile {
  const profileUser = response.data.attributes.user;
  if (!profileUser) {
    throw new Error('No user data in auth response');
  }

  // Merge top-level JSON:API id onto the user (id is not in attributes per
  // JSON:API spec) — same merge profile.service.ts does on /user.
  const profileData: ProfileData = {
    ...response.data.attributes,
    user: { ...profileUser, id: response.data.id },
  };

  return {
    user: {
      id: response.data.id,
      name: profileUser.name,
      email: profileUser.email,
      locale: profileUser.locale,
      is_email_verified: profileUser.is_email_verified,
      has_completed_renders: profileUser.has_completed_renders,
      should_request_feedback: profileUser.should_request_feedback,
      email_verified_at: profileUser.email_verified_at ?? null,
      created_at: profileUser.created_at,
      updated_at: profileUser.updated_at ?? profileUser.created_at,
      tokens_balance: response.data.attributes.tokens?.current_balance,
    },
    profileData,
  };
}

function transformAuthResponse(response: AuthResponse): AuthCredentials {
  const token = response.meta?.token;
  if (!token) {
    throw new Error('No token in auth response');
  }

  const { user, profileData } = parseProfileFromAuthResponse(response);

  return {
    user,
    token,
    profileData,
    defaultProjectId: response.meta?.default_project_id,
    isNewUser: response.meta?.is_new_user,
  };
}

class AuthService extends BaseApiService {
  private getDeviceName(): string {
    if (typeof window === 'undefined') return 'Server';

    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;

    // Try to detect device type
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return `iOS Device (${platform})`;
    } else if (/Android/.test(userAgent)) {
      return 'Android Device';
    } else if (/Mac/.test(platform)) {
      return `Mac (${platform})`;
    } else if (/Win/.test(platform)) {
      return `Windows (${platform})`;
    } else if (/Linux/.test(platform)) {
      return `Linux (${platform})`;
    }

    return `Web Browser (${platform || 'Unknown'})`;
  }

  async login(credentials: LoginRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...credentials,
          device_name: credentials.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/login',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.login' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async register(credentials: RegisterRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...credentials,
          device_name: credentials.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/register',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.register' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async logout(): Promise<void> {
    // Best-effort server-side revoke. AuthTokenService.clearAuth() runs in
    // auth.store regardless — local state is cleared even if the API call
    // fails (offline, 401 on already-expired token, etc.).
    try {
      await this.post('/v1/auth/logout', undefined, undefined, {
        error: { silent: true },
      });
    } catch (error) {
      authLogger.debug('Logout API call failed (continuing local cleanup):', error);
    }
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    await this.post('/v1/auth/password/forgot', payload, undefined, {
      loading: { trackEndpoint: 'auth.forgot-password' },
    });
  }

  async validateResetToken(data: ValidateResetTokenRequest): Promise<void> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    await this.post('/v1/auth/password/validate-token', payload, undefined, {
      loading: { trackEndpoint: 'auth.validate-token' },
    });
  }

  async resetPassword(data: ResetPasswordRequest): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...data,
          device_name: data.device_name || this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/password/reset',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.reset-password' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  /**
   * Exchange a Google OAuth authorization code (web flow) for an API token.
   * `redirect_uri` must match the one used to initiate the OAuth round-trip
   * (server validates it against the allowlist).
   */
  async googleCodeExchange(data: {
    code: string;
    redirect_uri: string;
  }): Promise<AuthCredentials> {
    const response = await this.post<AuthResponse>(
      '/v1/auth/google/code-exchange',
      data as unknown as Record<string, unknown>,
      undefined,
      {
        loading: { trackEndpoint: 'auth.google-code-exchange' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  /**
   * Exchange a VK ID authorization code (web redirect flow) for an API token.
   * The client generated the PKCE pair itself, so the verifier travels with
   * the code; `redirect_uri` must be the one the code was issued for (the API
   * checks it against its allowlist).
   */
  async vkCodeExchange(data: {
    code: string;
    code_verifier: string;
    device_id: string;
    redirect_uri: string;
  }): Promise<AuthCredentials> {
    const response = await this.post<AuthResponse>(
      '/v1/auth/vk/code-exchange',
      { platform: 'web', device_name: this.getDeviceName(), ...data },
      undefined,
      {
        loading: { trackEndpoint: 'auth.vk-code-exchange' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  // ---- Account from first launch (docs/identity/ADR-001) ----

  /**
   * Sign in with the browser. Throws an ApiClientError with code
   * SIGN_IN_METHOD_REQUIRED (409) when the server declines; the caller shows
   * the sign-in screen.
   */
  async deviceSignIn(body: Record<string, unknown>): Promise<AuthCredentials> {
    const response = await this.post<AuthResponse>('/v1/auth/device', body, undefined, {
      loading: { trackEndpoint: 'auth.device' },
      error: { silent: true },
    });
    return transformAuthResponse(response);
  }

  /** Register this browser on the signed-in account (after a sign-in). */
  async attachDevice(body: Record<string, unknown>): Promise<void> {
    await this.post<AuthResponse>('/v1/auth/methods/device', body, undefined, {
      error: { silent: true },
    });
  }

  private async addMethod(path: string, body: Record<string, unknown>): Promise<SignInMethodResult> {
    const response = await this.post<AuthResponse>(path, body, undefined, {
      loading: { trackEndpoint: 'auth.add-sign-in-method' },
      error: { silent: true },
    });
    return {
      credentials: transformAuthResponse(response),
      mergeMessage: response.meta?.account_merge?.message ?? null,
    };
  }

  addAppleMethod(data: {
    identity_token: string;
    authorization_code?: string;
    full_name?: string | null;
  }): Promise<SignInMethodResult> {
    return this.addMethod('/v1/auth/methods/apple', { device_name: 'web-apple-auth', ...data });
  }

  addGoogleMethod(data: { code: string; redirect_uri: string }): Promise<SignInMethodResult> {
    return this.addMethod('/v1/auth/methods/google', data);
  }

  addVkMethod(data: {
    code: string;
    code_verifier: string;
    device_id: string;
    redirect_uri: string;
  }): Promise<SignInMethodResult> {
    return this.addMethod('/v1/auth/methods/vk', { platform: 'web', ...data });
  }

  async sendSignInEmailCode(email: string): Promise<void> {
    await this.post('/v1/auth/methods/email/code', { email }, undefined, {
      error: { silent: true },
    });
  }

  addEmailMethod(email: string, code: string): Promise<SignInMethodResult> {
    return this.addMethod('/v1/auth/methods/email', { email, code });
  }

  linkCheckoutEmail(): Promise<SignInMethodResult> {
    return this.addMethod('/v1/auth/methods/email/checkout', {});
  }

  async dismissSignInSuggestion(): Promise<void> {
    await this.post('/v1/user/sign-in-suggestion/dismiss', {}, undefined, {
      error: { silent: true },
    });
  }

  /** Which optional sign-in providers the API offers this visitor. */
  async getProviders(): Promise<{ isVkIdAvailable: boolean }> {
    const response = await this.httpGet<{
      data: { attributes: { is_vk_id_available: boolean } };
    }>('/v1/auth/providers');
    return { isVkIdAvailable: response.data.attributes.is_vk_id_available };
  }

  /**
   * Exchange a one-time temp token (from the welcome e-mail's auto-login
   * link) for a real Sanctum token. Same wire shape as login/register, so
   * the shared transform applies. The temp token is consumed server-side on
   * the first attempt — callers must not retry a failed exchange.
   */
  async exchangeTempToken(tempToken: string): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: 'auth',
        attributes: {
          temp_token: tempToken,
          device_name: this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/exchange-token',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.exchange-token' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }

  async sendVerificationCode(): Promise<void> {
    await this.post('/v1/auth/verification-code/send');
  }

  async verifyEmailCode(
    code: string
  ): Promise<{
    user: AuthCredentials['user'];
    profileData: AuthCredentials['profileData'];
    defaultProjectId: string | null;
  }> {
    const payload = {
      data: {
        type: 'verification',
        attributes: { code },
      },
    };

    // verifyCode now returns the same UserProfileResource shape as /user —
    // the auth flow's last flat-shape endpoint is gone. Same parser as
    // login/register; only difference is the response carries no fresh
    // token (the caller is already authenticated via the existing session).
    const response = await this.post<AuthResponse>(
      '/v1/auth/verification-code/verify',
      payload,
      undefined,
      { error: { silent: false } }
    );

    const { user, profileData } = parseProfileFromAuthResponse(response);
    return {
      user,
      profileData,
      defaultProjectId: response.meta?.default_project_id ?? null,
    };
  }

  // Redirect to Google OAuth - returns the redirect URL
  async getGoogleOAuthUrl(): Promise<string> {
    const response = await this.httpGet<{
      data: { attributes: { redirect_url: string } };
    }>(
      '/v1/auth/social/google?app=web',
      undefined,
      {
        loading: { trackEndpoint: 'auth.google-oauth-url' },
      }
    );
    return response.data.attributes.redirect_url;
  }

  async validateActivationToken(data: {
    email: string;
    activation_token: string;
  }): Promise<{
    is_valid: boolean;
    already_activated: boolean;
    email: string;
    error?: string;
  }> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: data,
      },
    };

    const response = await this.post<{
      data: {
        attributes: {
          is_valid: boolean;
          already_activated: boolean;
          email: string;
          error?: string;
        };
      };
    }>('/v1/auth/activate/validate-token', payload, undefined, {
      loading: { trackEndpoint: 'auth.validate-activation' },
      error: { silent: false },
    });

    return response.data.attributes;
  }

  async activateAccount(data: {
    email: string;
    activation_token: string;
    password: string;
  }): Promise<AuthCredentials> {
    const payload = {
      data: {
        type: ClassTypes.USER,
        attributes: {
          ...data,
          device_name: this.getDeviceName(),
        },
      },
    };

    const response = await this.post<AuthResponse>(
      '/v1/auth/activate',
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'auth.activate' },
        error: { silent: false },
      }
    );

    return transformAuthResponse(response);
  }
}

export const authService = new AuthService();
export default authService;
